/**
 * Scene-traversal caches for the paper-space viewport render pass
 * (`Scene3DStage.tsx` `ViewportRenderOrchestrator`) — extracted so the
 * invalidation contract can be unit-tested without an R3F mount.
 *
 * The orchestrator needs three object sets on EVERY frame:
 *
 *   - `preHidable`  — model-space objects hidden for step 1 / 2b so model
 *                     geometry never paints on the bare sheet (including an
 *                     attributed viewport point-proxy cloud — Codex PR #784
 *                     round-12 P2, `rendering/viewportPointProxyStamp.ts`);
 *   - `allEntity`   — every entity-tagged object, hidden for the step-5
 *                     frame-only composite pass;
 *   - `overlays`    — the `isViewportOverlay` root groups.
 *
 * `scene.traverse` is O(#objects), so the sets are cached and rebuilt only
 * when the scene can no longer be described by them.
 *
 * # Invalidation keys (Codex PR #784 round-11 P1 — `Scene3DStage.tsx:271`)
 *
 *  1. **census changed** — the caller's `SpaceLayerCensusCache.sync` verdict
 *     (spatial identity / `geometryVersion` / satellite-map revision, plus a
 *     relift's `addSatellites` growth). The keyId-bucket fallback branch reads
 *     `modelLayers` / `paperLayers`, so a census move re-classifies buckets.
 *  2. **scene-root topology changed** — see {@link rootTopologySignature}.
 *  3. **a cached object was detached** — the MC4 staleness guard, retained for
 *     objects removed from a NESTED parent (which leaves the scene root, and
 *     therefore key 2, unchanged).
 *
 * ## Why key 2 exists
 *
 * A relift adds THREE objects to the scene WITHOUT moving any key the census
 * is built from: `FrameLoops` Pass 2 materialises a brand-new `LineSegments2`
 * bucket (`buildSingleBucketMesh`) and the on-demand resolver emits the
 * parent's / satellites' direct meshes (`emitReliftDirectMeshes` → hatch fill,
 * arrow decorators, array instances, and — asynchronously, after the font
 * promise resolves — glyph meshes). None of that bumps `geometryVersion`,
 * replaces the spatial index, or bumps the satellite-map revision; and when
 * the satellites sit on an ALREADY-KNOWN layer, `addSatellites` does not grow
 * the census either. Key 1 therefore reports "unchanged", and key 3 only ever
 * detects REMOVALS — so the new objects were absent from both the paper-space
 * pre-hide and the frame-only hide, and painted model geometry over the bare
 * sheet, outside every viewport scissor rect.
 *
 * Key 2 closes that at the consumer, in O(1) per frame, and — unlike an
 * explicit revision each emitter must remember to bump — it also covers the
 * ASYNC text channel, which attaches its meshes outside any frame callback
 * (`sceneBulkLoadHelpers.ts emitTextMeshes` → `scene.add` in a promise
 * continuation) and has no frame-loop seam from which to signal.
 *
 * The one shape it cannot see is an object added BELOW the scene root (into an
 * existing group). Every emitter today attaches at the root — the grep-able
 * contract is `scene.add(...)` in `entityRenderAttach.ts`,
 * `sceneBulkLoadHelpers.ts`, `SceneBulkLoad.ts` and `FrameLoops.tsx`. An
 * emitter that ever nests its objects must invalidate explicitly instead; the
 * store's `sync` verdict is the single seam to extend.
 */

import type * as THREE from "three";

import type { SpatialIndex } from "../types";
import { readViewportPointProxy } from "./viewportPointProxyStamp";
import { extractLayerFromKeyId } from "./viewportRenderPass";

/**
 * The two layer sets the keyId-bucket fallback reads off
 * `scene/spaceLayerCensusCache.ts` (structural, so this module does not depend
 * on the cache implementation).
 */
export interface SpaceLayerCensusView {
  readonly modelLayers: ReadonlySet<string>;
  readonly paperLayers: ReadonlySet<string>;
}

export interface ViewportTraversalCaches {
  /** Model-space objects pre-hidden for the bare-sheet render (step 1 / 2b). */
  readonly preHidable: readonly THREE.Object3D[];
  /** Every entity-tagged object — the step-5 frame-only hide set. */
  readonly allEntity: readonly THREE.Object3D[];
  /** `isViewportOverlay` root groups (paper sheet + VP frame). */
  readonly overlays: readonly THREE.Object3D[];
}

export interface ViewportTraversalCacheInputs {
  scene: THREE.Scene;
  spatial: SpatialIndex | null;
  census: SpaceLayerCensusView;
  /** This frame's `SpaceLayerCensusCache.sync` verdict (key 1). */
  censusChanged: boolean;
}

export interface ViewportTraversalCacheStore {
  /**
   * Return the current sets, rebuilding them when any invalidation key moved.
   * Steady state costs one `scene.children` length + tail-id comparison and
   * the detached-object scan — no traversal.
   */
  sync(inputs: ViewportTraversalCacheInputs): ViewportTraversalCaches;
}

/**
 * O(1) signature of the scene ROOT's child list: `(count, id of the last
 * child)`.
 *
 * `THREE.Object3D.id` is drawn from a process-global counter that only ever
 * increases, and `Object3D.add` APPENDS. So every object created and attached
 * after a signature was taken sorts AFTER every object that existed then. If
 * at least one such object is still attached when the next signature is taken,
 * the LAST child is one of them (removals only shorten the list; they never
 * reorder it), and its id differs from the recorded one. A signature that
 * compares equal therefore proves no root-level addition survives — which is
 * exactly the condition under which the cached sets still describe the scene.
 */
function rootTopologySignature(scene: THREE.Scene): {
  count: number;
  tailId: number;
} {
  const children = scene.children;
  const count = children.length;
  const tail = count > 0 ? children[count - 1] : undefined;
  return { count, tailId: tail ? tail.id : -1 };
}

/**
 * Walk the scene once and classify every object into the three sets. Pure —
 * exported for the store and for tests.
 */
export function buildViewportTraversalCaches(
  scene: THREE.Scene,
  spatial: SpatialIndex | null,
  census: SpaceLayerCensusView,
): ViewportTraversalCaches {
  const preHidable: THREE.Object3D[] = [];
  const allEntity: THREE.Object3D[] = [];
  const overlays: THREE.Object3D[] = [];
  const { modelLayers, paperLayers } = census;
  scene.traverse((obj) => {
    const ud = obj.userData as Record<string, unknown>;
    if (ud["isViewportOverlay"] === true) {
      overlays.push(obj);
      return;
    }
    // Collect all entity-tagged objects for step 5 (full scene hide).
    if (
      typeof ud["entityId"] === "string" ||
      typeof ud["keyId"] === "string" ||
      ud["kind"] === "block-insert-instanced" ||
      ud["kind"] === "lod-points-cloud"
    ) {
      allEntity.push(obj);
    }
    // Collect model-space-only objects for step 1 pre-hide.
    //
    // Codex PR #784 round-12 P2 — an ATTRIBUTED point-proxy cloud
    // (`rendering/viewportPointProxyStamp.ts`) holds MODEL-space dots a
    // paper-space viewport asked for; like any other model geometry it must
    // not paint on the bare sheet outside every VP scissor rect, and it is the
    // only points cloud that can say so (it carries its own `spaceId`, and by
    // construction every dot in it is model space). The MAIN camera's cloud is
    // deliberately untouched here: it has no `spaceId` stamp, so it keeps the
    // behaviour it has always had.
    if (ud["viewportPointProxy"] === true) {
      if (readViewportPointProxy(ud) !== null && ud["spaceId"] === "model") {
        preHidable.push(obj);
      }
    } else if (typeof ud["entityId"] === "string") {
      if (spatial?.resolve(ud["entityId"])?.spaceId === "model") {
        preHidable.push(obj);
      }
    } else if (ud["kind"] === "block-insert-instanced") {
      // iter-7 P1 #1: Only cache when ALL insertIds are model-space.
      const insertIds = ud["insertIds"] as string[] | undefined;
      const allModel =
        insertIds != null &&
        insertIds.length > 0 &&
        insertIds.every((id) => spatial?.resolve(id)?.spaceId === "model");
      if (allModel) {
        preHidable.push(obj);
      }
    } else if (typeof ud["keyId"] === "string") {
      // MC4 (model/paper separation) — hide a batched line bucket on
      // a layout when it is model-space geometry. The bucket mesh
      // carries its OWN `userData.spaceId` (`BucketSpaceClass`:
      // "model" | "paper"), tagged at build time from the
      // constituent entities' spaceIds (see `buildBucketMeshes`).
      // This is authoritative and robust for BOTH imported AND
      // from-scratch-drawn model entities, where the prior
      // model-ONLY-layer heuristic failed: a freshly drawn LINE on
      // the default layer "0" was not pre-hidden and leaked directly
      // onto the bare paper sheet.
      //
      //   - "model" → hide (model geometry must not draw on the
      //     bare sheet; it re-appears only inside VP scissor rects
      //     in step 4).
      //   - "paper" → keep (real paper-space content stays on the
      //     sheet).
      //
      // Codex round 15 P1 — there is no longer a third "mixed" class to
      // keep-and-hope-for-the-best. `batchedLayer.ts MeshKey.space` makes the
      // space part of the BUCKET IDENTITY, so a model line and a paper line
      // agreeing on layer × colour × linetype (layer "0", BYLAYER, Continuous)
      // no longer share a mesh, and the tag below decides every bucket
      // outright. That closes the leak this branch used to cause on a streamed
      // sheet: a mixed bucket could not be hidden without hiding its paper
      // half, so it was kept and the model half painted on the bare sheet.
      const space = ud["spaceId"];
      if (space === "model") {
        preHidable.push(obj);
      } else if (space !== "paper") {
        // UNTAGGED only — an object carrying a `keyId` that no
        // `buildSingleBucketMesh` built (a foreign / legacy mesh).
        // Every bucket this renderer builds is tagged, so this is the
        // defensive path; it preserves the pre-MC4 model-ONLY-layer
        // heuristic rather than guessing "paper" and leaking.
        const layer = extractLayerFromKeyId(ud["keyId"]);
        if (modelLayers.has(layer) && !paperLayers.has(layer)) {
          preHidable.push(obj);
        }
      }
    }
  });
  return { preHidable, allEntity, overlays };
}

export function createViewportTraversalCacheStore(): ViewportTraversalCacheStore {
  let caches: ViewportTraversalCaches | null = null;
  let count = -1;
  let tailId = -1;
  return {
    sync({ scene, spatial, census, censusChanged }) {
      const signature = rootTopologySignature(scene);
      const topologyChanged =
        signature.count !== count || signature.tailId !== tailId;
      count = signature.count;
      tailId = signature.tailId;
      // The `||` order matters for cost, not for correctness: the
      // detached-object scan (O(#allEntity)) is skipped entirely on a frame
      // that already decided to rebuild.
      if (
        caches === null ||
        censusChanged ||
        topologyChanged ||
        caches.allEntity.some((obj) => obj.parent === null)
      ) {
        caches = buildViewportTraversalCaches(scene, spatial, census);
      }
      return caches;
    },
  };
}
